/* FASE K2 — Box credenziali cliente auto-generate post-submit.
 *
 * Utilizzo condiviso da tutti i wizard (RCA/Casa/Vita/Salute/Viaggio/D&O/Cyber
 * /Fine Arts/RC Prof). Riceve `account` = {created, email, temp_password, ...}.
 *
 * Rende:
 *  1) Modal prominente al primo mount (dismissibile con "Ho salvato").
 *  2) Box inline persistente sotto (fallback + copy pulsante).
 *
 * Renderizza NULLA se `account?.created !== true` o mancano credenziali.
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Copy, Check, KeyRound, ShieldCheck, X, Eye, EyeOff } from "lucide-react";

export default function AccountCredentialsBox({ account }) {
  const created = !!(account && account.created && account.temp_password);
  const [modalOpen, setModalOpen] = useState(created);
  const [saved, setSaved] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    if (created) setModalOpen(true);
  }, [created]);

  if (!created) return null;
  const { email, temp_password } = account;

  const copy = async (val, field) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopiedField(field);
      toast.success(`${field === "email" ? "Email" : "Password"} copiata`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  return (
    <>
      {/* Modal prominente */}
      {modalOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4"
          data-testid="account-creds-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border-2 border-amber-300 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-slate-100"
              data-testid="account-creds-modal-close"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
            <div className="text-center mb-4">
              <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-3">
                <KeyRound className="w-8 h-8 text-amber-700" />
              </div>
              <h2 className="text-xl font-black text-[#163321] mb-1">
                Il tuo account cliente è pronto!
              </h2>
              <p className="text-sm text-slate-600">
                Salva queste credenziali. La password sarà mostrata{" "}
                <b>una sola volta</b>.
              </p>
            </div>

            <CredRow
              label="Email"
              value={email}
              copied={copiedField === "email"}
              onCopy={() => copy(email, "email")}
              tid="creds-email"
            />
            <CredRow
              label="Password temporanea"
              value={temp_password}
              copied={copiedField === "password"}
              onCopy={() => copy(temp_password, "password")}
              tid="creds-password"
              masked={!showPwd}
              onToggleMask={() => setShowPwd(!showPwd)}
            />

            <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Al primo accesso ti verrà chiesto di cambiare la password. Non
                condividere queste credenziali via email o chat non sicura.
              </span>
            </div>

            <button
              type="button"
              onClick={() => {
                setSaved(true);
                setModalOpen(false);
              }}
              data-testid="account-creds-modal-confirm"
              className="mt-4 w-full min-h-[48px] px-6 py-3 rounded-full bg-[#163321] text-white text-sm font-bold hover:bg-[#0e1f15] transition-all"
            >
              Ho salvato le credenziali
            </button>
          </div>
        </div>
      )}

      {/* Box inline persistente (fallback + riepilogo) */}
      <div
        className="mt-5 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 text-left"
        data-testid="account-creds-inline"
      >
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-5 h-5 text-amber-700" />
          <h3 className="text-base font-black text-[#163321]">
            Credenziali Area Cliente
          </h3>
        </div>
        <p className="text-xs text-slate-600 mb-3">
          Il tuo account è stato creato. Le credenziali sotto sono{" "}
          {saved ? "già state salvate" : "visibili una sola volta"}. Al primo
          login dovrai impostare una password personale.
        </p>
        <CredRow
          label="Email"
          value={email}
          copied={copiedField === "email"}
          onCopy={() => copy(email, "email")}
          tid="creds-inline-email"
          compact
        />
        <CredRow
          label="Password temporanea"
          value={temp_password}
          copied={copiedField === "password"}
          onCopy={() => copy(temp_password, "password")}
          tid="creds-inline-password"
          masked={!showPwd}
          onToggleMask={() => setShowPwd(!showPwd)}
          compact
        />
      </div>
    </>
  );
}

function CredRow({ label, value, copied, onCopy, tid, masked, onToggleMask, compact }) {
  return (
    <div className={compact ? "mb-2" : "mb-3"}>
      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">
        {label}
      </p>
      <div className="flex items-center gap-2">
        <code
          className="flex-1 px-3 py-2 rounded-lg bg-white border border-slate-300 font-mono text-sm font-bold text-[#163321] break-all"
          data-testid={tid}
        >
          {masked !== undefined && masked ? "•".repeat(Math.max(6, (value || "").length)) : value}
        </code>
        {masked !== undefined && (
          <button
            type="button"
            onClick={onToggleMask}
            className="min-w-[40px] min-h-[40px] inline-flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200"
            aria-label={masked ? "Mostra" : "Nascondi"}
            data-testid={`${tid}-toggle`}
          >
            {masked ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
        )}
        <button
          type="button"
          onClick={onCopy}
          data-testid={`${tid}-copy`}
          className="min-w-[40px] min-h-[40px] inline-flex items-center justify-center rounded-lg bg-[#163321] text-white hover:bg-[#0e1f15]"
          aria-label="Copia"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
